import { Grid } from '@mui/material';
import React from 'react';

function resolveContextName(children) {
  const arr = flattenChildren(children);

  for (const child of arr) {
    if (React.isValidElement(child)) {
      if (typeof child.type === 'function') {
        return child.type.displayName || child.type.name;
      }
    }
  }

  return 'UnknownCaller';
}

function flattenChildren(children) {
  const result = [];
  React.Children.forEach(children, child => {
    if (!child) return;
    if (Array.isArray(child)) {
      result.push(...flattenChildren(child));
    } else if (child.type === React.Fragment) {
      result.push(...flattenChildren(child.props.children));
    } else {
      result.push(child);
    }
  });
  return result;
}

const SectionGrid = ({
  children,
  centerChildren = false,
  padding = '16px',
  customLayout = {},
  gridSx = {},
  spacing = 2,
  mdVal = 6,
  customItemSx = {},
}) => {
  const breakpointsOrder = ['xs', 'sm', 'md', 'lg', 'xl'];
  const childArray = flattenChildren(children);

  const renderChildren = () => {
    return childArray.map((child, index) => {
      const childKey = index + 1;

      // ----- CALCULA EL ANCHO POR BREAKPOINT -----
      const widthStyles = {
        flexBasis: {},
        maxWidth: {},
      };

      breakpointsOrder.forEach((bp, bpIndex) => {
        let val;
        for (let i = bpIndex; i >= 0; i--) {
          const lowerBp = breakpointsOrder[i];
          if (customLayout?.[lowerBp]?.[childKey] !== undefined) {
            val = customLayout[lowerBp][childKey];
            break;
          }
        }

        if (val === undefined) {
          val = bp === 'xs' || bp === 'sm' ? 12 : mdVal;
        }

        const percent = (val / 12) * 100;
        const calcValue = `calc(${percent}%)`;
        widthStyles.flexBasis[bp] = calcValue;
        widthStyles.maxWidth[bp] = calcValue;
      });

      // ----- AGREGA ESTILOS PERSONALIZADOS -----
      const baseItemSx = customItemSx?.[childKey] || {};
      let mergedItemSx = { ...widthStyles };

      // Permite customItemSx[2] = { alignSelf: 'center' }
      // o customItemSx[2] = { md: { alignSelf: 'center' }, xs: { alignSelf: 'flex-start' } }
      breakpointsOrder.forEach(bp => {
        if (baseItemSx[bp]) {
          mergedItemSx[bp] = {
            ...(mergedItemSx[bp] || {}),
            ...baseItemSx[bp],
          };
        }
      });

      // Si el usuario pasó estilos directos (no por breakpoint)
      const hasDirectStyles = Object.keys(baseItemSx).some(
        key => !breakpointsOrder.includes(key)
      );

      const contextName = resolveContextName(children);

      if (hasDirectStyles) {
        mergedItemSx = { ...mergedItemSx, ...baseItemSx };
      }

      // ----- RENDERIZA EL HIJO -----
      if (
        React.isValidElement(child) &&
        child.type === Grid &&
        child.props.item
      ) {
        return React.cloneElement(child, {
          sx: { ...child.props.sx, ...mergedItemSx },
          key: childKey,
          id: childKey,
        });
      }

      return (
        <Grid
          item
          sx={mergedItemSx}
          key={childKey}
          id={`sectiongrid-${contextName}-${childKey}`}
        >
          {child}
        </Grid>
      );
    });
  };

  return (
    <Grid
      container
      spacing={spacing}
      justifyContent={centerChildren ? 'center' : 'flex-start'}
      sx={{ padding, ...gridSx }}
    >
      {renderChildren()}
    </Grid>
  );
};

export default SectionGrid;
